'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { 
  ArrowRight, 
  MapPin,
  Star,
  Users,
  Award,
  ShieldCheck,
  Heart,
  Clock,
  TrendingUp,
  Leaf,
  Camera,
  Play,
  Quote
} from 'lucide-react';
import { Button } from '@myapp/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@myapp/ui/components/card';
import { Badge } from '@myapp/ui/components/badge';

// 농가 스토리 데이터 (uglyus.co.kr와 유사한 스타일)
const farmStories = [
  {
    id: 1,
    farmName: '평창 행복농장',
    farmerName: '김평창',
    region: '평창군',
    specialty: '감자, 옥수수',
    title: '3대째 이어온 감자 농사 이야기',
    subtitle: '고랭지 청정 환경에서 키우는 달콤한 감자',
    story: '할아버지 때부터 시작된 우리 농장은 평창의 깨끗한 공기와 맑은 물로 감자를 키우고 있습니다. 농약 없이 자연 그대로 키운 감자는 달콤하고 포슬포슬한 식감이 일품이에요.',
    image: '👨‍🌾',
    backgroundImage: '🏔️',
    rating: 4.8,
    reviews: 124,
    yearsOfFarming: 35,
    verified: true,
    tags: ['유기농', '3대가업', '친환경'],
    achievements: ['강원도 우수농가', '친환경 인증', '품질 보증'],
    philosophy: '자연과 함께, 정성과 사랑으로',
    location: '강원도 평창군 대관령면',
    products: [
      { name: '평창 감자', price: '15,000원/5kg', season: '6-10월' },
      { name: '대관령 옥수수', price: '12,000원/10개', season: '7-9월' }
    ],
    missionType: 'quality',
    socialImpact: '지역 청년 일자리 3개 창출',
    featured: true
  },
  {
    id: 2,
    farmName: '횡성 청정한우',
    farmerName: '박횡성',
    region: '횡성군',
    specialty: '한우',
    title: '자연방목으로 키운 건강한 한우',
    subtitle: '넓은 초원에서 스트레스 없이 자란 프리미엄 한우',
    story: '횡성의 넓은 초원에서 자유롭게 뛰어노는 한우들을 키우고 있습니다. 항생제나 성장촉진제 없이, 오직 좋은 풀과 깨끗한 물만으로 키운 건강한 한우입니다.',
    image: '👨‍🌾',
    backgroundImage: '🐄',
    rating: 4.9,
    reviews: 89,
    yearsOfFarming: 20,
    verified: true,
    tags: ['자연방목', '무항생제', '1++등급'],
    achievements: ['횡성한우 브랜드', 'HACCP 인증', '품질보증서'],
    philosophy: '동물복지와 품질을 모두 잡은 한우 사육',
    location: '강원도 횡성군 우천면',
    products: [
      { name: '횡성한우 구이용', price: '89,000원/600g', season: '연중' },
      { name: '횡성한우 불고기용', price: '75,000원/500g', season: '연중' }
    ],
    missionType: 'premium',
    socialImpact: '지역 축산업 발전 기여',
    featured: true
  },
  {
    id: 3,
    farmName: '속초 바다농장',
    farmerName: '이동해',
    region: '속초시',
    specialty: '수산물',
    title: '동해 청정해역의 신선한 수산물',
    subtitle: '새벽바다에서 갓 잡은 싱싱한 해산물',
    story: '매일 새벽 3시에 배를 타고 나가 동해 청정해역에서 오징어와 생선을 잡습니다. 잡자마자 바로 급속냉동해서 신선함을 그대로 유지합니다.',
    image: '👨‍⚓',
    backgroundImage: '🌊',
    rating: 4.7,
    reviews: 156,
    yearsOfFarming: 25,
    verified: true,
    tags: ['당일수확', '급속냉동', '청정해역'],
    achievements: ['수협 우수어가', '신선도 보장', '당일배송'],
    philosophy: '바다가 주는 신선함을 그대로',
    location: '강원도 속초시 청호동',
    products: [
      { name: '속초 오징어', price: '25,000원/5마리', season: '3-11월' },
      { name: '동해 생선세트', price: '35,000원/2kg', season: '연중' }
    ],
    missionType: 'freshness',
    socialImpact: '어촌 경제 활성화 기여',
    featured: false
  },
  {
    id: 4,
    farmName: '정선 산골농장',
    farmerName: '최정선',
    region: '정선군',
    specialty: '산나물',
    title: '깊은 산속에서 자라는 건강한 산나물',
    subtitle: '정선 깊은 산골에서 자연 그대로 키운 산채',
    story: '정선의 깊은 산속에서 곤드레, 취나물, 도라지 등을 자연 그대로 키우고 있습니다. 산 속 맑은 공기와 깨끗한 물로 자란 산나물은 영양이 풍부해요.',
    image: '👩‍🌾',
    backgroundImage: '⛰️',
    rating: 4.6,
    reviews: 67,
    yearsOfFarming: 15,
    verified: true,
    tags: ['야생재배', '무농약', '산지직송'],
    achievements: ['정선군 우수농가', '전통농법 보존', '품질인증'],
    philosophy: '자연이 주는 건강한 선물',
    location: '강원도 정선군 신동읍',
    products: [
      { name: '정선 곤드레', price: '20,000원/1kg', season: '4-6월' },
      { name: '산나물 세트', price: '35,000원/2kg', season: '3-10월' }
    ],
    missionType: 'natural',
    socialImpact: '산촌 문화 보존 및 전수',
    featured: false
  },
  {
    id: 5,
    farmName: '인제 황금농장',
    farmerName: '황인제',
    region: '인제군',
    specialty: '황태, 산채',
    title: '청정 인제에서 만드는 황태 이야기',
    subtitle: '겨울 강추위가 만드는 최고급 황태',
    story: '인제의 깨끗한 공기와 강추위를 이용해 최고 품질의 황태를 만들고 있습니다. 한겨울 밤새 얼고 낮에 녹기를 반복하며 만들어지는 황태는 단백질이 풍부해요.',
    image: '👨‍🍳',
    backgroundImage: '❄️',
    rating: 4.5,
    reviews: 234,
    yearsOfFarming: 30,
    verified: true,
    tags: ['전통제법', '자연건조', '고품질'],
    achievements: ['인제황태 명인', '전통식품 인증', '지역특산품'],
    philosophy: '자연의 추위가 만드는 건강식품',
    location: '강원도 인제군 북면',
    products: [
      { name: '인제 황태', price: '45,000원/20마리', season: '11-3월' },
      { name: '황태포', price: '25,000원/300g', season: '연중' }
    ],
    missionType: 'traditional',
    socialImpact: '전통 식문화 계승 및 보전',
    featured: false
  },
  {
    id: 6,
    farmName: '양양 바람농장',
    farmerName: '송양양',
    region: '양양군',
    specialty: '송이버섯, 산채',
    title: '양양 청정 숲에서 자라는 송이버섯',
    subtitle: '깊은 소나무 숲에서 자연 발생하는 프리미엄 송이',
    story: '양양의 깨끗한 소나무 숲에서 자연적으로 발생하는 송이버섯을 채취합니다. 인공재배가 불가능한 송이버섯은 자연이 주는 최고의 선물입니다.',
    image: '👩‍🌾',
    backgroundImage: '🌲',
    rating: 4.4,
    reviews: 91,
    yearsOfFarming: 18,
    verified: true,
    tags: ['자연채취', '프리미엄', '한정수량'],
    achievements: ['양양송이 전문가', '품질보증', '산림청 허가'],
    philosophy: '숲이 주는 귀한 선물을 정성으로',
    location: '강원도 양양군 서면',
    products: [
      { name: '양양 송이버섯', price: '150,000원/1kg', season: '9-10월' },
      { name: '산채나물 세트', price: '30,000원/1.5kg', season: '4-8월' }
    ],
    missionType: 'premium',
    socialImpact: '산림자원 보호 및 활용',
    featured: false
  }
];

const missionTypes = {
  quality: { label: '품질혁신', color: 'bg-blue-500', icon: '🏆' },
  premium: { label: '프리미엄', color: 'bg-purple-500', icon: '💎' },
  freshness: { label: '신선보장', color: 'bg-green-500', icon: '🌱' },
  natural: { label: '자연친화', color: 'bg-green-600', icon: '🍃' },
  traditional: { label: '전통계승', color: 'bg-amber-500', icon: '🏮' }
};

export default function FarmersPage() {
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedFarmer, setSelectedFarmer] = useState(null);
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  // 2주 카운트다운 계산 (서버 시간 기준)
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const nextUpdate = new Date();
      nextUpdate.setDate(now.getDate() + 14); // 2주 후
      nextUpdate.setHours(0, 0, 0, 0); // 자정으로 설정
      
      const difference = nextUpdate.getTime() - now.getTime();
      
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000)
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, []);

  const filteredStories = selectedFilter === 'all' 
    ? farmStories 
    : farmStories.filter(story => story.missionType === selectedFilter);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50">
      {/* Header */}
      <section className="bg-gradient-to-r from-green-600 to-blue-600 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="mb-4 bg-white/20 border-white/30 text-white">
              <Users className="w-3 h-3 mr-1" />
              농가 이야기
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              강원도 농가들의 진짜 이야기
            </h1>
            <p className="text-xl text-green-50 mb-6">
              정직하게 재배하는 강원도 농가들의 특별한 스토리를 만나보세요
            </p>
            
            {/* 카운트다운 타이머 */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-white/20">
              <div className="text-center">
                <p className="text-sm text-green-100 mb-4">
                  📅 다음 농가 스토리 업데이트까지
                </p>
                <div className="flex justify-center gap-4">
                  <div className="bg-white/20 rounded-lg p-3 min-w-[60px]">
                    <div className="text-2xl font-bold text-white">{timeLeft.days}</div>
                    <div className="text-xs text-green-100">일</div>
                  </div>
                  <div className="bg-white/20 rounded-lg p-3 min-w-[60px]">
                    <div className="text-2xl font-bold text-white">{String(timeLeft.hours).padStart(2, '0')}</div>
                    <div className="text-xs text-green-100">시간</div>
                  </div>
                  <div className="bg-white/20 rounded-lg p-3 min-w-[60px]">
                    <div className="text-2xl font-bold text-white">{String(timeLeft.minutes).padStart(2, '0')}</div>
                    <div className="text-xs text-green-100">분</div>
                  </div>
                  <div className="bg-white/20 rounded-lg p-3 min-w-[60px]">
                    <div className="text-2xl font-bold text-white animate-pulse">{String(timeLeft.seconds).padStart(2, '0')}</div>
                    <div className="text-xs text-green-100">초</div>
                  </div>
                </div>
                <p className="text-xs text-green-200 mt-3">
                  ✨ 새로운 농가 이야기와 신선한 특산물을 만나보세요!
                </p>
              </div>
            </div>
            
            <div className="flex items-center justify-center gap-8 text-sm">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5" />
                <span>농가 직거래 인증</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5" />
                <span>품질 보증 시스템</span>
              </div>
              <div className="flex items-center gap-2">
                <Leaf className="w-5 h-5" />
                <span>친환경 농업</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Filter */}
      <section className="bg-white border-b py-4 sticky top-[73px] z-40">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-4 overflow-x-auto">
            <span className="text-sm font-medium text-gray-700 whitespace-nowrap">미션별 농가:</span>
            <Button
              variant={selectedFilter === 'all' ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedFilter('all')}
              className="text-xs whitespace-nowrap"
            >
              전체 ({farmStories.length})
            </Button>
            {Object.entries(missionTypes).map(([key, mission]) => {
              const count = farmStories.filter(story => story.missionType === key).length;
              return (
                <Button
                  key={key}
                  variant={selectedFilter === key ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedFilter(key)}
                  className="text-xs whitespace-nowrap"
                >
                  {mission.icon} {mission.label} ({count})
                </Button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Stories */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              <TrendingUp className="w-8 h-8 text-green-500 inline mr-2" />
              농가 스토리
            </h2>
            <p className="text-gray-600">
              각각의 농가가 가진 특별한 이야기와 철학을 만나보세요
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredStories.map((story) => (
              <Card key={story.id} className="group hover:shadow-xl transition-all duration-300 overflow-hidden relative">
                {/* Background Pattern */}
                <div className="absolute top-0 right-0 text-6xl opacity-10 transform rotate-12 -mr-4 -mt-4">
                  {story.backgroundImage}
                </div>
                
                <div className="absolute top-3 right-3 z-10">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="bg-white/80 hover:bg-white text-gray-600 rounded-full p-2"
                  >
                    <Heart className="w-4 h-4" />
                  </Button>
                </div>

                <CardHeader className="pb-3 relative z-10">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center text-2xl">
                      {story.image}
                    </div>
                    <div>
                      <CardTitle className="text-lg text-gray-900">{story.farmName}</CardTitle>
                      <div className="flex items-center text-sm text-gray-600">
                        <MapPin className="w-3 h-3 mr-1" />
                        {story.region}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {story.verified && (
                        <ShieldCheck className="w-4 h-4 text-green-500" />
                      )}
                      <Badge 
                        className={`text-xs text-white ${missionTypes[story.missionType]?.color || 'bg-gray-500'}`}
                      >
                        {missionTypes[story.missionType]?.icon} {missionTypes[story.missionType]?.label}
                      </Badge>
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Star className="w-3 h-3 text-yellow-500 fill-current mr-1" />
                      {story.rating}
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 group-hover:text-green-600 transition-colors mb-2">
                    {story.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    {story.subtitle}
                  </p>
                </CardHeader>

                <CardContent className="pt-0 relative z-10">
                  <div className="mb-4">
                    <Quote className="w-4 h-4 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-700 italic line-clamp-3">
                      "{story.story}"
                    </p>
                  </div>

                  <div className="mb-4">
                    <div className="flex items-center gap-4 text-xs text-gray-500 mb-2">
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {story.yearsOfFarming}년 경력
                      </div>
                      <div className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {story.reviews}개 후기
                      </div>
                    </div>
                    
                    <div className="text-xs text-green-600 font-medium mb-2">
                      📍 {story.philosophy}
                    </div>
                    
                    <div className="text-xs text-blue-600 font-medium">
                      💡 {story.socialImpact}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-1 mb-4">
                    {story.tags.map((tag, index) => (
                      <Badge key={index} variant="outline" className="text-xs px-2 py-0.5 bg-green-50 border-green-200 text-green-700">
                        {tag}
                      </Badge>
                    ))}
                  </div>

                  <div className="mb-4">
                    <div className="text-xs font-semibold text-gray-800 mb-2">주요 생산품</div>
                    <div className="space-y-1">
                      {story.products.slice(0, 2).map((product, index) => (
                        <div key={index} className="text-xs text-gray-600 flex justify-between">
                          <span>{product.name}</span>
                          <span className="font-medium">{product.price}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="flex items-center text-sm text-gray-500">
                        <Heart className="w-3 h-3 mr-1" />
                        {story.reviews} 좋아요
                      </div>
                      {story.featured && (
                        <Badge className="bg-green-500 text-white text-xs px-2 py-1">
                          ✨ 추천
                        </Badge>
                      )}
                    </div>
                    <Button 
                      size="sm" 
                      className="bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white"
                      onClick={() => setSelectedFarmer(story)}
                    >
                      상세보기
                      <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredStories.length === 0 && (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🌾</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                해당하는 농가가 없습니다
              </h3>
              <p className="text-gray-600">
                다른 카테고리를 선택해보세요
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Farmer Detail Modal */}
      {selectedFarmer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center text-3xl">
                    {selectedFarmer.image}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{selectedFarmer.farmName}</h2>
                    <p className="text-gray-600">{selectedFarmer.farmerName} 농장주</p>
                    <div className="flex items-center gap-2 mt-1">
                      <MapPin className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-600">{selectedFarmer.location}</span>
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  onClick={() => setSelectedFarmer(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </Button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">농가 스토리</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    {selectedFarmer.story}
                  </p>
                  
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-800 mb-2">농가 철학</h4>
                    <p className="text-green-600 font-medium italic">"{selectedFarmer.philosophy}"</p>
                  </div>

                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-800 mb-2">사회적 임팩트</h4>
                    <p className="text-blue-600">{selectedFarmer.socialImpact}</p>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {selectedFarmer.achievements.map((achievement, index) => (
                      <Badge key={index} className="bg-yellow-100 text-yellow-800 border-yellow-200">
                        <Award className="w-3 h-3 mr-1" />
                        {achievement}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">생산 정보</h3>
                  
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">경력</span>
                        <div className="font-semibold">{selectedFarmer.yearsOfFarming}년</div>
                      </div>
                      <div>
                        <span className="text-gray-600">평점</span>
                        <div className="font-semibold flex items-center">
                          <Star className="w-4 h-4 text-yellow-500 fill-current mr-1" />
                          {selectedFarmer.rating}
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-600">후기</span>
                        <div className="font-semibold">{selectedFarmer.reviews}개</div>
                      </div>
                      <div>
                        <span className="text-gray-600">전문분야</span>
                        <div className="font-semibold">{selectedFarmer.specialty}</div>
                      </div>
                    </div>
                  </div>

                  <h4 className="font-semibold text-gray-800 mb-3">주요 생산품</h4>
                  <div className="space-y-3">
                    {selectedFarmer.products.map((product, index) => (
                      <div key={index} className="border rounded-lg p-3">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-medium text-gray-900">{product.name}</span>
                          <span className="text-green-600 font-bold">{product.price}</span>
                        </div>
                        <div className="text-sm text-gray-600">수확시기: {product.season}</div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6">
                    <Button className="w-full bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white">
                      <Camera className="w-4 h-4 mr-2" />
                      농장 방문하기
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-green-600 to-blue-600 py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            농가와 직접 만나보세요!
          </h2>
          <p className="text-green-50 mb-8 max-w-2xl mx-auto">
            강원도 농가들의 정성스런 농산물을 직거래로 만나보세요
          </p>
          <Link href="/seasonal">
            <Button size="lg" className="bg-white text-green-600 hover:bg-gray-50 px-8 py-3 text-lg rounded-xl">
              제철 특산물 보러가기
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}